"use client";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { MapPin, ArrowRight, User, LoaderCircle } from "lucide-react";
import HowItWorksAccordion from "../components/landing/HowItWorksAccordion";
import FixedLogo from "../components/landing/FixedLogo";
import QRCodeWidget from "../components/landing/QRCodeWidget";
import { AuthModal } from "@/components/auth";
import { LanguageToggle } from "@/components/layout";
import { useTranslation } from "@/lib/i18n";
import { useAuthStore } from "@/store/auth.store";
import { useAddressStore } from "@/store/address.store";
import { useGeolocation } from "@/hooks/use-geolocation";
import { useAddressAutocomplete } from "@/hooks/use-addresses";
import type { AddressAutocompleteResult } from "@/types";
import { useLocaleStore } from "@/store/locale.store";

/* ─────────────────────────────────────────────────────────────
   Marketing copy (sections not yet covered by the app's i18n
   dictionary: help cards, products, FAQ, delivery, jobs, footer).
   Functional copy (login/signup/address search) now comes from
   useTranslation() -> t.landing.* instead, see below.
───────────────────────────────────────────────────────────── */

type Lang = "fr" | "en";

const marketingCopy = {
  fr: {
    nav: {
      howItWorks: "FAQ",
      deliveryAreas: "Zones de livraison",
      joinUs: "Nous rejoindre",
    },
    hero: {
      line1: "Vos repas livrés",
      line2: "chez vous",
    },
    help: {
      heading: "Comment Fast peut vous aider\u00a0?",
      cards: [
        { title: "Une grosse faim à 22h et la flemme de cuisiner ?", cta: "Livré en 30 min !" },
        { title: "Envie d'un bon resto ou d'un bon burger ?", cta: "Commandez, on arrive !" },
        { title: "Ton plat préféré sans bouger du canapé ?", cta: "On s'occupe de tout !" },
      ],
    },
    products: {
      heading: "Large choix de produits",
      sub: "Au prix du marché local \u2014 pas de surprises.",
      badge: "N°1 sur Fast",
      ctaLabel: "Catégories\ndisponibles",
    },
    howItWorks: { heading: "Foire aux questions" },
    delivery: {
      heading: "Zones de livraison",
      sub: "On s\u2019étend à travers l\u2019Algérie. Vérifiez si Fast livre dans votre quartier.",
      soon: "+ bientôt\u2026",
      cta: "Vérifier ma zone",
    },
    work: {
      heading: "Travailler chez Fast",
      livreurRole: "Devenir\nLivreur",
      livreurDesc: "Rejoignez notre équipe de Fasteurs et gagnez bien en livrant dans votre quartier.",
      partenaireRole: "Devenir\nPartenaire",
      partenaireDesc: "Vous avez un restaurant ou une épicerie ? Rejoignez Fast et touchez des milliers de clients dans votre ville.",
      learnMore: "En savoir plus →",
    },
    footer: {
      ctaLine1: "Téléchargez l'app ",
      ctaSub: "Obtenez ce dont vous avez besoin, quand vous en avez besoin.",
      privacy: "Confidentialité",
      terms: "CGU",
      jobs: "Emplois",
    },
  },
  en: {
    nav: {
      howItWorks: "FAQ",
      deliveryAreas: "Delivery areas",
      joinUs: "Join Us",
    },
    hero: {
      line1: "Your meals delivered",
      line2: "to you",
    },
    help: {
      heading: "How can Fast help you?",
      cards: [
        { title: "Starving at 10 PM and can't be bothered to cook?", cta: "Delivered in 30 min!" },
        { title: "Craving a good restaurant or a great burger?", cta: "Order, we're on our way!" },
        { title: "Your favorite dish without leaving the couch?", cta: "We've got it covered!" },
      ],
    },
    products: {
      heading: "Wide product selection",
      sub: "At local market prices \u2014 no surprises.",
      badge: "N°1 on Fast",
      ctaLabel: "Categories\navailable",
    },
    howItWorks: { heading: "Frequently Asked Questions" },
    delivery: {
      heading: "Delivery areas",
      sub: "We\u2019re expanding across Algeria. Check if Fast delivers in your neighborhood.",
      soon: "+ coming soon\u2026",
      cta: "Check my area",
    },
    work: {
      heading: "Work at Fast",
      livreurRole: "Become a\nDriver",
      livreurDesc: "Join our team of Fasteurs and earn well delivering in your neighborhood.",
      partenaireRole: "Become a\nPartner",
      partenaireDesc: "Own a restaurant or grocery store? Join Fast and reach thousands of customers in your city.",
      learnMore: "Learn more →",
    },
    footer: {
      ctaLine1: "Download the app",
      ctaSub: "Get what you need, when you need it.",
      privacy: "Privacy",
      terms: "T&Cs",
      jobs: "Jobs",
    },
  },
};

type MarketingT = typeof marketingCopy.fr;

export type Category = {
  ulid: string;
  name: string;
  icon: string;
};
export const CATEGORIES: Category[] = [
  { ulid: "01JZN30X95R3EWJF68EWAVWKAC", name: "Poulet",    icon: "/categories/01KN7HR12PP5FX3SE83R2N2AER-chicken_2.webp" },
  { ulid: "01JZN30XCP3Z2TM29JFEPXKYC4", name: "Courses",   icon: "/categories/01KN7HNXGR1ZVBY1TS9ZDNDB2V-course.webp" },
  { ulid: "01JZN30X23H5961Z0CB51H1PD8", name: "Pizzas",    icon: "/categories/01KN7HNB7X4GPA2WB01BS3WDH8-pizza.webp" },
  { ulid: "01JZN30X5MVZ7FPTJ214M15NYN", name: "Burgers",   icon: "/categories/01KN7HPE0NQPGQGA88PQ730WX0-burger_2.webp" },
  { ulid: "01JZN30Y1VTTC5JPGE1DH8YTMC", name: "Tacos",     icon: "/categories/01KN7HPTQRRC3HKAJQ1DV1GGX3-taco.webp" },
  { ulid: "01JZN30YY5AH5MYPED06H923SC", name: "Sandwichs", icon: "/categories/01KN7HQAEBZXH615CPMDM30175-sandwich.webp" },
  { ulid: "01JZN30XYB7DRNJ3R4SAQCED5V", name: "Asiatique", icon: "/categories/01KN7HRPCTW95FNM45SG4CZVGZ-sushi_2.webp" },
  { ulid: "01JZN30Y5CGHTQNNTC595DJ4D1", name: "Kebabs",    icon: "/categories/01KN7HS3ZJDJXTH7R6JJWM2VG3-Kebab.webp" },
];

/* ─────────────────────────────────────────────────────────────
   App-Store / Google-Play badge buttons
───────────────────────────────────────────────────────────── */
function AppStoreButton({ lang }: { lang: Lang }) {
  const src = lang === "fr" ? "/Download_on_the_App_Store_Badge_FRCA_RGB_blk.png" : "/Download_on_the_App_Store_Badge.png";
  return (
    <a href="https://apps.apple.com/dz/app/fast-livraison-repas-courses/id1469020170?l=fr-FR" target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store" className="hover:opacity-80 transition-opacity">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={src} alt="Download on the App Store" className="block h-[50px] w-[160px] md:h-[60px] md:w-[180px]" />
    </a>
  );
}

function GooglePlayButton({ lang }: { lang: Lang }) {
  const src = lang === "fr" ? "/googleplayFR.png" : "/Google_Play_Store_badge_EN.png";
  return (
    <a href="https://play.google.com/store/apps/details?id=com.customer.fastdelivery&hl=fr" target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play" className="hover:opacity-80 transition-opacity">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={src} alt="Get it on Google Play" className="block h-[50px] w-[160px] md:h-[60px] md:w-[180px]" />
    </a>
  );
}

/* ─────────────────────────────────────────────────────────────
   Phone Mockup
───────────────────────────────────────────────────────────── */
function PhoneMockup({ scale = 1 }: { scale?: number }) {
  const W = 430;
  const H = 932;
  const w = Math.round(W * scale);
  const h = Math.round(H * scale);
  const outerRadius = Math.round(54 * scale);
  const bezel = Math.round(9 * scale);
  const screenRadius = Math.round(46 * scale);
  const hiW = Math.round(120 * scale);
  const hiH = Math.round(5 * scale);
  const hiBottom = Math.round(10 * scale);

  return (
    <div
      className="relative mx-auto flex-shrink-0"
      style={{
        width: w,
        height: h,
        borderRadius: outerRadius,
        background: "#676b73df",
        boxShadow: `0 ${Math.round(32*scale)}px ${Math.round(64*scale)}px rgba(0,0,0,0.55), 0 ${Math.round(2*scale)}px ${Math.round(6*scale)}px rgba(255,255,255,0.08) inset, 0 -${Math.round(1*scale)}px ${Math.round(3*scale)}px rgba(255,255,255,0.06) inset`,
        padding: bezel,
      }}
    >
      <div
        className="relative w-full h-full px-10 overflow-hidden"
        style={{ borderRadius: screenRadius, backgroundColor: "#000" }}
      >
        <Image
          src="/image.png"
          alt="Fast App screenshot"
          fill
          className="object-cover object-top"
          sizes="260px"
          priority
        />
        <div
          className="absolute left-1/2 -translate-x-1/2 rounded-full z-10"
          style={{
            bottom: hiBottom,
            width: hiW,
            height: hiH,
            backgroundColor: "rgba(255,255,255,0.35)",
          }}
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Navbar — real login/signup + language toggle, plus marketing
   anchor links from the old design.
───────────────────────────────────────────────────────────── */
function Navbar({
  navT,
  landingT,
  onAuthOpen,
  isAuthed,
  onEnterApp,
}: {
  navT: MarketingT["nav"];
  landingT: ReturnType<typeof useTranslation>["landing"];
  onAuthOpen: () => void;
  isAuthed: boolean;
  onEnterApp: () => void;
}) {
  return (
    <nav
      className="w-full px-6 md:px-12 py-4 flex items-center border-0"
      style={{ backgroundColor: "var(--fast-secondary)" }}
    >
      <div className="md:hidden flex-shrink-0 mr-auto">
        <Image
          src="/fast-logo.svg"
          alt="Fast App"
          width={80}
          height={27}
          style={{ filter: "brightness(0) invert(1)" }}
        />
      </div>
      <div className="hidden md:flex flex-1 justify-center">
        <div className="flex pl-48 text-primary py-4 items-center gap-8">
          {[
            [navT.howItWorks, "#how-it-works"],
            [navT.deliveryAreas, "#delivery-areas"],
            [navT.joinUs, "#work-at-fast"],
          ].map(([label, href]) => (
            <a
              key={href}
              href={href}
              className="nav-link text-sm font-semibold uppercase tracking-widest"
            >
              {label}
            </a>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 sm:gap-3">
        <LanguageToggle className="border-white/40 bg-white/10 backdrop-blur" />
        {isAuthed ? (
          /* Already signed in — send them into the app instead of asking to log in. */
          <button
            onClick={onEnterApp}
            className="rounded-full bg-white dark:bg-[#2A2A2A] px-3 py-2 text-sm font-bold text-secondary dark:text-primary shadow transition hover:shadow-md sm:px-5"
          >
            {landingT.goToApp}
          </button>
        ) : (
          <>
            <button
              onClick={onAuthOpen}
              className="rounded-full bg-white dark:bg-[#2A2A2A] px-3 py-2 text-sm font-bold text-secondary dark:text-primary shadow transition hover:shadow-md sm:px-5"
            >
              {landingT.login}
            </button>
            <button
              onClick={onAuthOpen}
              className="hidden rounded-full border-2 border-white px-3 py-2 text-sm font-bold text-white transition hover:bg-white/10 sm:inline-block sm:px-5"
            >
              {landingT.signup}
            </button>
          </>
        )}
      </div>
    </nav>
  );
}

/* ─────────────────────────────────────────────────────────────
   Hero — phone mockup + download badges (new page) merged with
   address-autocomplete search + login CTAs (old page).
───────────────────────────────────────────────────────────── */
function HeroSection({
  heroT,
  landingT,
  lang,
  onAuthOpen,
  isAuthed,
}: {
  heroT: MarketingT["hero"];
  landingT: ReturnType<typeof useTranslation>["landing"];
  lang: Lang;
  onAuthOpen: () => void;
  isAuthed: boolean;
}) {
  const router = useRouter();
  const setGuestAddress = useAddressStore((s) => s.setGuestAddress);

  const [addressQuery, setAddressQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(addressQuery.trim()), 300);
    return () => clearTimeout(timeout);
  }, [addressQuery]);

  // Don't prompt for GPS on the landing page; bias autocomplete by the
  // default region and let the real location resolve on /home.
  const { coords } = useGeolocation(false);
  const { data: rawResults, isFetching } = useAddressAutocomplete({
    query: debouncedQuery,
    country: "dz",
    latitude: coords?.latitude,
    longitude: coords?.longitude,
    limit: 5,
  });
  const raw = rawResults as unknown;
  const results: AddressAutocompleteResult[] | null = Array.isArray(raw)
    ? (raw as AddressAutocompleteResult[])
    : Array.isArray((raw as { data?: AddressAutocompleteResult[] })?.data)
    ? (raw as { data: AddressAutocompleteResult[] }).data
    : null;

  const pickAddress = (r: AddressAutocompleteResult) => {
    setGuestAddress({ latitude: r.latitude, longitude: r.longitude, formatted: r.formatted, origin: "picked" });
    router.push("/home");
  };

  const handleSubmit = () => {
    if (results && results.length > 0) {
      pickAddress(results[0]);
    } else {
      router.push("/home");
    }
  };

  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: "var(--fast-secondary)" }}
    >
      {/* ── Mobile layout ── */}
      <div className="md:hidden flex flex-col px-6 pt-10" style={{ height: "85dvh" }}>
        <div
          className="font-black text-4xl md:text-6xl text-center uppercase leading-[0.9] mb-5"
          style={{ color: "#fff" }}
        >
          {heroT.line1}
          <br />
          <span style={{ color: "var(--fast-primary)" }}>{heroT.line2}</span>
        </div>

        {/* Address search */}
        <div className="mb-4">
          {/* Le champ demandait une adresse sans jamais dire à quoi elle sert.
              Les magasins sont classés par distance : sans ce point, on ne peut
              montrer que le centre d'Alger — autant l'expliquer ici plutôt que
              de laisser le client conclure qu'on ne livre pas chez lui. */}
          <p className="mb-2 text-center text-xs text-white/70">
            Votre adresse nous sert à n&apos;afficher que les restaurants qui livrent
            réellement chez vous.
          </p>
          <div className="relative w-full text-left">
            <div className="flex items-center rounded-full bg-white dark:bg-[#2A2A2A] shadow-lg border border-black/10">
              <div className="flex items-center pl-4 text-gray-400 dark:text-gray-500">
                <MapPin className="h-5 w-5" />
              </div>
              <input
                type="text"
                value={addressQuery}
                onChange={(e) => setAddressQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                placeholder={landingT.addressPlaceholder}
                className="flex-1 bg-transparent px-3 py-3.5 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none"
              />
              {isFetching && <LoaderCircle className="h-4 w-4 animate-spin text-gray-400 mr-1" />}
              <button
                onClick={handleSubmit}
                className="m-1.5 flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-white transition hover:bg-secondary/90"
                aria-label={landingT.continue}
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
            {results && results.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 z-30 overflow-hidden rounded-2xl border border-gray-100 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] shadow-xl">
                {results.map((r) => (
                  <button
                    key={r.place_id}
                    onClick={() => pickAddress(r)}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-gray-50 dark:hover:bg-[#313131]"
                  >
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <span className="text-sm leading-snug text-gray-800 dark:text-gray-200">{r.formatted}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center gap-2 mb-6">
          {isAuthed ? (
            <button
              onClick={() => router.push("/home")}
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-5 py-2 text-sm font-medium text-white/80 transition hover:bg-white/10"
            >
              <User className="h-4 w-4" />
              {landingT.continueToApp}
            </button>
          ) : (
            <>
              <button
                onClick={onAuthOpen}
                className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-5 py-2 text-sm font-medium text-white/80 transition hover:bg-white/10"
              >
                <User className="h-4 w-4" />
                {landingT.loginForSavedAddress}
              </button>
              <button
                onClick={() => router.push("/home")}
                className="text-sm text-white/60 hover:text-white transition underline underline-offset-2"
              >
                {landingT.browseWithoutAccount}
              </button>
            </>
          )}
        </div>

        <div className="flex gap-3 mb-8 justify-center">
          <GooglePlayButton lang={lang} />
          <AppStoreButton lang={lang} />
        </div>

        <div className="flex-1 flex justify-center items-end">
          <PhoneMockup scale={0.64} />
        </div>
      </div>

      {/* ── Desktop layout ── */}
      <div className="hidden md:flex max-w-7xl xl:max-w-7xl mx-auto px-6 py-24 flex-row gap-0 max-h-[75dvh]">
        <div className="w-1/2 flex justify-start items-start">
          <PhoneMockup />
        </div>
        <div className="w-1/2 px-12 md:px-6 flex flex-col items-center text-center gap-8">
          <div className="text-7xl font-black uppercase leading-none" style={{ color: "#fff" }}>
            {heroT.line1}
            <br />
            <span style={{ color: "var(--fast-primary)" }}>{heroT.line2}</span>
          </div>

          {/* Address search */}
          <div className="w-full text-left relative">
            <div className="flex items-center rounded-full bg-white dark:bg-[#2A2A2A] shadow-lg border border-black/10">
              <div className="flex items-center pl-4 text-gray-400 dark:text-gray-500">
                <MapPin className="h-5 w-5" />
              </div>
              <input
                type="text"
                value={addressQuery}
                onChange={(e) => setAddressQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                placeholder={landingT.addressPlaceholder}
                className="flex-1 bg-transparent px-3 py-3.5 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none"
              />
              {isFetching && <LoaderCircle className="h-4 w-4 animate-spin text-gray-400 mr-1" />}
              <button
                onClick={handleSubmit}
                className="m-1.5 flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-white transition hover:bg-secondary/90"
                aria-label={landingT.continue}
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
            {results && results.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 z-30 overflow-hidden rounded-2xl border border-gray-100 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] shadow-xl">
                {results.map((r) => (
                  <button
                    key={r.place_id}
                    onClick={() => pickAddress(r)}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-gray-50 dark:hover:bg-[#313131]"
                  >
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <span className="text-sm leading-snug text-gray-800 dark:text-gray-200">{r.formatted}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            {isAuthed ? (
              <button
                onClick={() => router.push("/home")}
                className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-5 py-2 text-sm font-medium text-white/80 transition hover:bg-white/10"
              >
                <User className="h-4 w-4" />
                {landingT.continueToApp}
              </button>
            ) : (
              <>
                <button
                  onClick={onAuthOpen}
                  className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-5 py-2 text-sm font-medium text-white/80 transition hover:bg-white/10"
                >
                  <User className="h-4 w-4" />
                  {landingT.loginForSavedAddress}
                </button>
                <button
                  onClick={() => router.push("/home")}
                  className="text-sm text-white/60 hover:text-white transition underline underline-offset-2"
                >
                  {landingT.browseWithoutAccount}
                </button>
              </>
            )}
          </div>

          <div className="w-full flex flex-wrap justify-center gap-3">
            <GooglePlayButton lang={lang} />
            <AppStoreButton lang={lang} />
          </div>
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
        style={{ background: "linear-gradient(to bottom, transparent, rgba(3,51,26,0.6))" }}
      />
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   Remaining marketing sections — unchanged from the new page.
───────────────────────────────────────────────────────────── */

function HowFastHelpsSection({ t }: { t: MarketingT["help"] }) {
  const cardIcons = [
    <svg key="fridge" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect x="18" y="8" width="28" height="48" rx="5" stroke="currentColor" strokeWidth="3" fill="none"/>
      <rect x="22" y="14" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="2.5" fill="none"/>
      <line x1="22" y1="34" x2="42" y2="34" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="22" y1="40" x2="36" y2="40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>,
    <svg key="dome" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M12 36 Q32 14 52 36" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round"/>
      <rect x="10" y="36" width="44" height="6" rx="3" stroke="currentColor" strokeWidth="2.5" fill="none"/>
      <line x1="32" y1="42" x2="32" y2="52" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="24" y1="52" x2="40" y2="52" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>,
    <svg key="cart" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M10 18 L14 18 L20 42 H50 L55 26 H18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <circle cx="24" cy="50" r="3.5" stroke="currentColor" strokeWidth="2.5" fill="none"/>
      <circle cx="44" cy="50" r="3.5" stroke="currentColor" strokeWidth="2.5" fill="none"/>
    </svg>,
  ];

  return (
    <section id="section-help" className="bg-white pt-16 pb-8 px-6 md:px-12">
      <div className="max-w-4xl xl:max-w-5xl mx-auto">
        <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter scale-y-[1.1] inline-block w-full text-left md:text-center mb-10 md:mb-16" style={{ color: "var(--fast-secondary)" }}>
          {t.heading}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {t.cards.map((c, idx) => (
            <div
              key={idx}
              className="relative flex flex-col items-start md:items-center text-left md:text-center gap-4 px-6 py-10 md:px-8 md:py-14 rounded-3xl overflow-hidden min-h-[180px] md:min-h-[340px] justify-center"
            >
              <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{ color: "var(--fast-primary)", opacity: 0.13 }}
              >
                <div style={{ width: 220, height: 220 }}>{cardIcons[idx]}</div>
              </div>
              <div className="relative flex flex-col gap-5">
                <p className="text-xl md:text-2xl font-bold text-black/75 leading-snug">{c.title}</p>
                <p className="text-xl md:text-2xl font-black" style={{ color: "var(--fast-secondary)" }}>{c.cta}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductsSection({ t }: { t: MarketingT["products"] }) {
  const categories = CATEGORIES;
  const [hero, ...rest] = categories;
  const topRight = rest.slice(0, 4);
  const bottomRow = rest.slice(4);
  const tileGradient = "linear-gradient(to top, rgba(245,245,243,1) 0%, rgba(245,245,243,0.88) 45%, transparent 100%)";

  return (
    <section id="section-products" className="bg-white pb-16 pt-8 px-6 md:px-12">
      <div className="max-w-4xl xl:max-w-5xl mx-auto">
        <div className="text-left md:text-center mb-10 md:mb-12">
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter scale-y-[1.1] inline-block w-full" style={{ color: "var(--fast-secondary)" }}>
            {t.heading}
          </h2>
          <p className="mt-3 text-lg text-black/50 font-medium">{t.sub}</p>
        </div>

        {/* Mobile */}
        <div className="flex flex-col gap-3 md:hidden">
          {hero && (
            <div className="relative w-full rounded-[22px] overflow-hidden" style={{ height: 200, backgroundColor: "#03331a" }}>
              <Image
                src={hero.icon}
                alt={hero.name}
                fill
                sizes="100vw"
                className="object-contain object-center"
                style={{ opacity: 0.88, transform: "scale(1.1)", transformOrigin: "center 40%" }}
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #03331a 0%, rgba(3,51,26,0.82) 28%, rgba(3,51,26,0.08) 65%, transparent 100%)" }} />
              <div className="absolute bottom-0 left-0 p-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] mb-1" style={{ color: "var(--fast-primary)" }}>{t.badge}</p>
                <h3 className="text-2xl font-black uppercase text-white leading-none tracking-tight">{hero.name}</h3>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {rest.slice(0, 2).map((cat) => (
              <div key={cat.ulid} className="relative rounded-[18px] overflow-hidden" style={{ height: 140, backgroundColor: "#f5f5f3" }}>
                <div className="absolute inset-0 flex items-center justify-center" style={{ paddingBottom: 24 }}>
                  <Image src={cat.icon} alt={cat.name} width={80} height={80} className="object-contain drop-shadow-sm" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 px-3 pb-2 pt-6" style={{ background: tileGradient }}>
                  <span className="text-[10px] font-black uppercase tracking-wide" style={{ color: "var(--fast-secondary)" }}>{cat.name}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {rest[2] && (
              <div className="relative rounded-[18px] overflow-hidden" style={{ height: 140, backgroundColor: "#f5f5f3" }}>
                <div className="absolute inset-0 flex items-center justify-center" style={{ paddingBottom: 24 }}>
                  <Image src={rest[2].icon} alt={rest[2].name} width={80} height={80} className="object-contain drop-shadow-sm" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 px-3 pb-2 pt-6" style={{ background: tileGradient }}>
                  <span className="text-[10px] font-black uppercase tracking-wide" style={{ color: "var(--fast-secondary)" }}>{rest[2].name}</span>
                </div>
              </div>
            )}
            <div className="rounded-[18px] flex flex-col items-center justify-center gap-1" style={{ height: 140, backgroundColor: "var(--fast-primary)" }}>
              <span className="text-3xl font-black leading-none" style={{ color: "var(--fast-secondary)" }}>30+</span>
              <span className="text-[10px] font-black uppercase tracking-widest text-center leading-tight whitespace-pre-line" style={{ color: "var(--fast-secondary)", opacity: 0.65 }}>{t.ctaLabel}</span>
            </div>
          </div>
        </div>

        {/* Desktop */}
        <div className="hidden md:flex flex-col gap-4">
          <div className="flex gap-4">
            {hero && (
              <div className="relative w-1/2 flex-shrink-0 rounded-[28px] overflow-hidden cursor-pointer group" style={{ height: 376, backgroundColor: "#03331a" }}>
                <Image
                  src={hero.icon}
                  alt={hero.name}
                  fill
                  sizes="(max-width: 768px) 50vw, 512px"
                  className="object-contain object-center transition-transform duration-700 group-hover:scale-110"
                  style={{ opacity: 0.88, transform: "scale(1.1)", transformOrigin: "center 40%" }}
                  priority
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #03331a 0%, rgba(3,51,26,0.82) 28%, rgba(3,51,26,0.08) 65%, transparent 100%)" }} />
                <div className="absolute bottom-0 left-0 p-7">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] mb-2" style={{ color: "var(--fast-primary)" }}>{t.badge}</p>
                  <h3 className="text-3xl md:text-4xl font-black uppercase text-white leading-none tracking-tight">{hero.name}</h3>
                </div>
              </div>
            )}
            <div className="flex-1 grid grid-cols-2 gap-4 content-start">
              {topRight.map((cat) => (
                <div key={cat.ulid} className="relative rounded-[22px] overflow-hidden cursor-pointer group" style={{ height: 178, backgroundColor: "#f5f5f3" }}>
                  <div className="absolute inset-0 flex items-center justify-center" style={{ paddingBottom: 28 }}>
                    <Image src={cat.icon} alt={cat.name} width={100} height={100} className="object-contain drop-shadow-md transition-transform duration-300 group-hover:scale-110" style={{ opacity: 0.95 }} />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 px-4 pb-3 pt-8" style={{ background: tileGradient }}>
                    <span className="text-[11px] font-black uppercase tracking-wide" style={{ color: "var(--fast-secondary)" }}>{cat.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            {bottomRow.map((cat) => (
              <div key={cat.ulid} className="relative rounded-[22px] overflow-hidden cursor-pointer group" style={{ height: 182, backgroundColor: "#f5f5f3" }}>
                <div className="absolute inset-0 flex items-center justify-center" style={{ paddingBottom: 28 }}>
                  <Image src={cat.icon} alt={cat.name} width={100} height={100} className="object-contain drop-shadow-md transition-transform duration-300 group-hover:scale-110" style={{ opacity: 0.95 }} />
                </div>
                <div className="absolute bottom-0 left-0 right-0 px-4 pb-3 pt-8" style={{ background: tileGradient }}>
                  <span className="text-xs font-black uppercase tracking-wide" style={{ color: "var(--fast-secondary)" }}>{cat.name}</span>
                </div>
              </div>
            ))}
            <div className="rounded-[22px] flex flex-col items-center justify-center gap-2 cursor-pointer hover:scale-[1.02] transition-transform duration-200" style={{ height: 182, backgroundColor: "var(--fast-primary)", opacity: 0.9 }}>
              <span className="text-4xl font-black leading-none" style={{ color: "var(--fast-secondary)" }}>30+</span>
              <span className="text-[11px] font-black uppercase tracking-widest text-center leading-tight whitespace-pre-line" style={{ color: "var(--fast-secondary)", opacity: 0.65 }}>{t.ctaLabel}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection({ t, lang }: { t: MarketingT["howItWorks"]; lang: Lang }) {
  return (
    <section id="how-it-works" className="py-16 px-6 md:px-12" style={{ backgroundColor: "#f5f5f3" }}>
      <div className="max-w-4xl xl:max-w-5xl text-left md:text-center mx-auto">
        <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter scale-y-[1.1] inline-block w-full mb-4 md:mb-12" style={{ color: "var(--fast-secondary)" }}>
          {t.heading}
        </h2>
        <HowItWorksAccordion lang={lang} />
      </div>
    </section>
  );
}

function DeliveryAreasSection({ t }: { t: MarketingT["delivery"] }) {
  // Linked, not decorative: these were <span>s, which made the homepage a dead
  // end — it passed no authority to the zone pages it advertises. Slugs match
  // lib/cities.ts. Blida is listed in the marketing copy but has no stores yet,
  // so it stays a plain chip rather than linking to a page that would 404.
  const cities: { name: string; slug?: string }[] = [
    { name: "Alger", slug: "alger" },
    { name: "Oran", slug: "oran" },
    { name: "Blida" },
    { name: "Boumerdès", slug: "boumerdes" },
    { name: "Annaba", slug: "annaba" },
  ];
  return (
    <section id="delivery-areas" className="py-16 px-6 md:px-12" style={{ backgroundColor: "var(--fast-secondary)" }}>
      <div className="max-w-4xl xl:max-w-5xl mx-auto text-center">
        <h2 className="text-3xl md:text-5xl text-left md:text-center font-black uppercase tracking-tighter scale-y-[1.1] inline-block w-full mb-4" style={{ color: "var(--fast-primary)" }}>
          {t.heading}
        </h2>
        <p className="text-lg text-left md:text-center font-medium mb-6 md:mb-12" style={{ color: "rgba(255,255,255,0.6)" }}>{t.sub}</p>
        <div className="flex flex-wrap justify-start md:justify-center gap-2 md:gap-3 mb-12">
          {cities.map((city) => {
            const chip = "rounded-full px-4 md:px-5 py-2.5 text-sm font-bold uppercase tracking-wider";
            const style = {
              backgroundColor: "rgba(8,243,119,0.15)",
              color: "var(--fast-primary)",
              border: "1.5px solid rgba(8,243,119,0.3)",
            };
            return city.slug ? (
              <Link
                key={city.name}
                href={`/livraison/${city.slug}`}
                className={`${chip} transition-opacity hover:opacity-80`}
                style={style}
              >
                {city.name}
              </Link>
            ) : (
              <span key={city.name} className={chip} style={style}>
                {city.name}
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WorkAtFastSection({ t }: { t: MarketingT["work"] }) {
  return (
    <section id="work-at-fast" className="py-16 bg-white px-6 md:px-12">
      <div className="max-w-4xl xl:max-w-5xl mx-auto">
        <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter scale-y-[1.1] inline-block w-full text-left md:text-center mb-12" style={{ color: "var(--fast-secondary)" }}>
          {t.heading}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            {
              role: t.livreurRole,
              desc: t.livreurDesc,
              icon: (
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                  <circle cx="13" cy="48" r="9" stroke="currentColor" strokeWidth="3.5"/>
                  <circle cx="13" cy="48" r="2.5" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="51" cy="48" r="8" stroke="currentColor" strokeWidth="3.5"/>
                  <circle cx="51" cy="48" r="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M5 43 Q5 30 13 29 Q17 29 20 32" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round"/>
                  <path d="M10 34 Q17 27 27 31" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round"/>
                  <path d="M20 32 Q25 32 27 36 L29 43 L43 43" stroke="currentColor" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M43 43 L46 34 Q47 30 48 27" stroke="currentColor" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
                  <path d="M48 27 Q50 38 51 40" stroke="currentColor" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
                  <line x1="48" y1="27" x2="46" y2="21" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  <line x1="40" y1="21" x2="54" y2="21" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
                </svg>
              ),
              href: "https://airtable.com/appRgIpNYmxzW7FXt/shr82KDhaNfW4H8QF",
            },
            {
              role: t.partenaireRole,
              desc: t.partenaireDesc,
              icon: (
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                  <polyline points="4,28 32,6 60,28" stroke="currentColor" strokeWidth="3.5" strokeLinejoin="round" strokeLinecap="round" fill="none"/>
                  <rect x="10" y="28" width="44" height="30" rx="2" stroke="currentColor" strokeWidth="3.5"/>
                  <path d="M24 58 L24 42 Q24 38 32 38 Q40 38 40 42 L40 58" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  <rect x="13" y="34" width="8" height="7" rx="1.5" stroke="currentColor" strokeWidth="2.5"/>
                  <rect x="43" y="34" width="8" height="7" rx="1.5" stroke="currentColor" strokeWidth="2.5"/>
                  <rect x="44" y="10" width="6" height="12" rx="1" stroke="currentColor" strokeWidth="2.5"/>
                </svg>
              ),
              href: "https://airtable.com/appDmDtpvQW3f0xNW/pagXKiCSpt5aHKHoJ/form",
            },
          ].map((job) => (
            <a
              key={job.role}
              href={job.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex flex-col justify-end rounded-3xl overflow-hidden p-8 md:p-10 min-h-[280px] md:min-h-[360px]"
              style={{ backgroundColor: "var(--fast-secondary)" }}
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ backgroundColor: "rgba(8,243,119,0.07)" }} />
              <div className="relative">
                <h3 className="text-4xl md:text-5xl font-black uppercase leading-tight whitespace-pre-line tracking-tighter" style={{ color: "#fff" }}>{job.role}</h3>
                <p className="mt-4 text-base text-white/55 font-medium leading-relaxed max-w-xs">{job.desc}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-black uppercase tracking-wider" style={{ color: "var(--fast-primary)" }}>{t.learnMore}</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function FooterCTA({ t, lang }: { t: MarketingT["footer"]; lang: Lang }) {
  return (
    <footer id="download" className="px-6 md:px-12" style={{ backgroundColor: "var(--fast-secondary)" }}>
      <div className="py-12 max-w-4xl xl:max-w-5xl mx-auto text-center">
        <h2 className="text-3xl md:text-6xl font-black uppercase tracking-tighter scale-y-[1.1] w-full leading-none mb-4 flex items-center justify-center gap-3 md:gap-5 flex-wrap" style={{ color: "#fff" }}>
          <span>{t.ctaLine1}</span>
          <div
            className="w-[140px] md:w-[140px] h-[40px] md:h-[65px]"
            style={{
              backgroundColor: "var(--fast-primary)",
              maskImage: "url(/fast-logo.svg)",
              maskSize: "100% 100%",
              maskRepeat: "no-repeat",
              maskPosition: "center",
              WebkitMaskImage: "url(/fast-logo.svg)",
              WebkitMaskSize: "100% 100%",
              WebkitMaskRepeat: "no-repeat",
              WebkitMaskPosition: "center",
            }}
            aria-label="Fast App"
          />
        </h2>
        <p className="text-lg md:text-2xl font-semibold my-10" style={{ color: "rgba(255,255,255,0.55)" }}>{t.ctaSub}</p>
        <div className="flex gap-2 md:gap-4 justify-center">
          <GooglePlayButton lang={lang} />
          <AppStoreButton lang={lang} />
        </div>
      </div>
      <div className="py-4 md:py-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <Image src="/fast-logo.svg" alt="Fast App" width={72} height={24} style={{ filter: "brightness(0) invert(1)", opacity: 0.5 }} />
        <div className="flex items-center gap-6 text-sm text-white/40 font-medium">
          <a href="#faq" className="hover:text-white/70">FAQ</a>
          <a href="#privacy" className="hover:text-white/70">{t.privacy}</a>
          <a href="#terms" className="hover:text-white/70">{t.terms}</a>
          <a href="#jobs" className="hover:text-white/70">{t.jobs}</a>
        </div>
        <p className="text-sm text-white/30 font-medium">© SARL HELLO DELIVERY {new Date().getFullYear()}</p>
      </div>
    </footer>
  );
}

/* ─────────────────────────────────────────────────────────────
   Page
───────────────────────────────────────────────────────────── */
export function LandingPage() {
  const [authOpen, setAuthOpen] = useState(false);
  const router = useRouter();
  const { landing: landingT } = useTranslation();
  const locale = useLocaleStore((s) => s.locale);
  const user = useAuthStore((s) => s.user);

  // Marketing sections (help/products/faq/delivery/work/footer) aren't in
  // the app's i18n dictionary yet, so they key off the same locale here.
  const lang = (locale as Lang) ?? "fr";
  const mt = marketingCopy[lang];

  // Un visiteur connecté est renvoyé sur /home par `LandingRedirect`, sauf
  // avec `?vitrine=1`. Dans ce cas de figure il consulte délibérément la page
  // marketing, donc les appels à l'action mènent à l'application plutôt que de
  // proposer une connexion qu'il a déjà faite.

  return (
    <div className="font-sans flex flex-col">
      {/* One document heading for the page. The hero renders its brand line
          twice (a mobile block and a desktop block, each hidden at the other
          breakpoint), so neither can be the <h1> without the other becoming a
          display:none heading. This states what the page is, once. */}
      <h1 className="sr-only">
        Fast — livraison de repas et de courses à domicile en Algérie
      </h1>
      <FixedLogo />
      <QRCodeWidget />
      <div style={{ backgroundColor: "var(--fast-secondary)" }}>
        <Navbar navT={mt.nav} landingT={landingT} onAuthOpen={() => setAuthOpen(true)} isAuthed={!!user} onEnterApp={() => router.push("/home")} />
        <HeroSection heroT={mt.hero} landingT={landingT} lang={lang} onAuthOpen={() => setAuthOpen(true)} isAuthed={!!user} />
      </div>
      <HowFastHelpsSection t={mt.help} />
      <ProductsSection t={mt.products} />
      <DeliveryAreasSection t={mt.delivery} />
      <WorkAtFastSection t={mt.work} />
      <HowItWorksSection t={mt.howItWorks} lang={lang} />
      <FooterCTA t={mt.footer} lang={lang} />

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onSuccess={() => router.push("/home")}
      />
    </div>
  );
}

export default LandingPage;